import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// material import
import {
  Box,
  CircularProgress,
  Divider,
  Grid2,
  Typography,
  Zoom,
} from "@mui/material";

// project import
import CheckCircleTwoToneIcon from "@mui/icons-material/CheckCircleTwoTone";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CustomTable from "../../ui-components/customTable";
import Header2 from "../../ui-components/headerii";
import MpgsServices from "../../services/v1/mpgsServices";
import EmailServices from "../../services/v1/emailServices";

const SponsorConfirmation = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(true);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const navigate = useNavigate();

  const formatDate = (dateString) => {
    const [day, month, year] = dateString.split("/");
    return `${year}-${month}-${day}`;
  };

  const columns = [
    { label: "Reservation No", field: "reservation_number" },
    { label: "Donor Name", field: "donor_name" },
    { label: "Package Name", field: "package_name" },
    { label: "Reserved Date", field: "reserved_date" },
    { label: "Total", field: "total" },
    { label: "Payment Method", field: "payment_method" },
  ];

  useEffect(() => {
    const handlePaymentResult = async () => {
      setProcessing(true);

      const urlParams = new URLSearchParams(location.search);
      const resultIndicator = urlParams.get("resultIndicator");
      const sessionVersion = urlParams.get("sessionVersion");
      const storedSuccessIndicator = sessionStorage.getItem(
        "mpgsSuccessIndicator"
      );

      // Get payment details from sessionStorage or location state
      const paymentDetails = JSON.parse(
        sessionStorage.getItem("paymentDetails") || "{}"
      );

      // resultIndicator only proves to the browser that this redirect matches
      // the checkout session we started -- it's not an id MPGS can look up
      // later. The backend independently re-verifies the payment against
      // MPGS's own records before trusting it, and needs the real order id
      // (saved in paymentDetails.orderId when checkout started) to do that,
      // not resultIndicator.
      if (resultIndicator && resultIndicator === storedSuccessIndicator) {
        try {
          const transactionData = {
            reservation_number: paymentDetails.reservation_number,
            donorID: paymentDetails.donorID,
            reservedDate: paymentDetails.reserved_date,
            transactionID: paymentDetails.orderId,
          };

          await MpgsServices.storeTransaction(transactionData);

          const paymentData = {
            reservation_number: paymentDetails.reservation_number,
            transactionID: paymentDetails.orderId,
            donor_name: paymentDetails.donor_name,
            package_name: paymentDetails.package_name,
            reserved_date: paymentDetails.reserved_date,
            total: paymentDetails.total,
            payment_method: paymentDetails.payment_method,
            donor_email: paymentDetails.donor_email,
          };

          await EmailServices.sendPaymentConfirmation(paymentData);

          setPaymentSuccess(true);
          setPaymentData({
            reservation_number: paymentDetails.reservation_number || "N/A",
            donor_name: paymentDetails.donor_name || "N/A",
            package_name: paymentDetails.package_name || "N/A",
            reserved_date: paymentDetails.reserved_date || "N/A",
            total: paymentDetails.total || "N/A",
            payment_method: paymentDetails.payment_method,
          });
        } catch (error) {
          console.error("Transaction confirmation failed:", error);
          setPaymentSuccess(false);
        }
      }
      setProcessing(false);
    };

    handlePaymentResult();
  }, []);

  return (
    <Box align="center">
      <Header2 />
      <Grid2 container justifyContent="center">
        <Grid2 item size={{ xs: 8.2 }}>
          {processing ? (
            <Box alignContent="center" sx={{ mt: 10, fontSize: 30 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid2 item>
              <Box sx={{ mt: 5 }}>
                {paymentSuccess ? (
                  <CheckCircleTwoToneIcon
                    color="success"
                    sx={{ fontSize: 60 }}
                  />
                ) : (
                  <ErrorOutlineIcon color="error" sx={{ fontSize: 60 }} />
                )}

                <Typography variant="h5" sx={{ fontWeight: "bold", mt: 2 }}>
                  {paymentSuccess
                    ? "Thank you for your Reservation."
                    : "Payment Processing Error."}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{ fontWeight: "medium", mt: 3 }}
                >
                  {paymentSuccess ? (
                    <>
                      Thank you for confirming your sponsorship for the Suwa
                      Arana - Place for Healing
                      <br />
                      Your sponsorship and payment details have been
                      successfully recorded. Please check your email for a copy
                      of your invoice and sponsorship information. We will send
                      reminders as your reserved date approaches to help you
                      stay informed.
                      <br />
                      Thank you again for your invaluable support.
                      <br />
                      <br />
                      Need to change your reserved date later? You can{" "}
                      <span
                        style={{ color: "#8B2757", cursor: "pointer", textDecoration: "underline" }}
                        onClick={() => navigate("/?openChangeDetails=1")}
                      >
                        manage it here
                      </span>
                      .
                    </>
                  ) : (
                    <>
                      We apologize, but there was an error processing your
                      payment.
                      <br />
                      Please try again or contact our support team for
                      assistance.
                    </>
                  )}
                </Typography>
              </Box>

              {paymentSuccess && paymentData && (
                <Grid2 item size={{ xs: 12 }}>
                  <Divider
                    variant="middle"
                    sx={{
                      maxWidth: "100%",
                      border: "1px dashed lightgrey",
                      mt: 3,
                      mb: 5,
                    }}
                  />
                  <CustomTable
                    columns={columns}
                    rows={[paymentData]}
                    hideFooter
                  />
                </Grid2>
              )}
              <Box sx={{ mb: 10 }} />
            </Grid2>
          )}
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default SponsorConfirmation;
